/**
 * EntityConnectivityPanel — V23.14.17-19
 *
 * Affiche sur la fiche entité Annuaire :
 *  - les signaux récents (Trend + Radar) qui mentionnent l'acteur
 *  - les actifs des modules îlots rattachés (Realestate / Marchés financiers / Sources Capital)
 *  - le compteur de cross-références (risques, chaînes, opportunités, utility maps)
 *
 * Tout est lazy-fetched. Vide → message neutre.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radio, Building2, Coins, Briefcase, Link2, ExternalLink, RefreshCw, Loader2 } from "lucide-react";

export default function EntityConnectivityPanel({ entityId, entityName }) {
  const [signals, setSignals] = useState(null);
  const [crossRefs, setCrossRefs] = useState(null);
  const [linkedAssets, setLinkedAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sigRes, refRes, assetsRes] = await Promise.allSettled([
        api.get(`/workspace/annuaire/entities/${entityId}/signals?limit=10`),
        api.get(`/workspace/annuaire/entities/${entityId}/cross-references`),
        api.get(`/workspace/annuaire/entities/${entityId}/linked-assets`),
      ]);
      setSignals(sigRes.status === "fulfilled" ? sigRes.value.data : null);
      setCrossRefs(refRes.status === "fulfilled" ? refRes.value.data : null);
      setLinkedAssets(assetsRes.status === "fulfilled" ? assetsRes.value.data : null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const rescan = async () => {
    setRescanning(true);
    try {
      await api.post(`/workspace/annuaire/entities/signals/rescan`);
      await load();
    } finally {
      setRescanning(false);
    }
  };

  if (loading) {
    return (
      <div className="noam-card p-4 text-zinc-400 text-xs flex items-center gap-2" data-testid="entity-connectivity-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Connexions cross-module…
      </div>
    );
  }

  const totalRefs = crossRefs?.total ?? 0;
  const sigItems = signals?.items || [];
  const assets = linkedAssets?.items || {};
  const assetTotals = linkedAssets?.totals || { total: 0 };

  return (
    <div className="noam-card p-4 space-y-4" data-testid="entity-connectivity-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[#378ADD]" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">Connexions cross-module</span>
        </div>
        <button
          onClick={rescan}
          disabled={rescanning}
          className="text-[10px] text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="entity-connectivity-rescan"
        >
          {rescanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Re-scan
        </button>
      </div>

      {/* Compteur cross-refs */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="entity-connectivity-refs">
        <Counter label="Risques" value={crossRefs?.risks ?? 0} />
        <Counter label="Chaînes" value={crossRefs?.chains_nodes ?? 0} />
        <Counter label="Opps" value={crossRefs?.origination_opps ?? 0} />
        <Counter label="UM" value={crossRefs?.utility_maps ?? 0} />
        <Counter label="Signaux" value={crossRefs?.signals ?? 0} highlight />
        <Counter label="Immo" value={crossRefs?.re_assets ?? 0} />
        <Counter label="Marchés fin." value={crossRefs?.brvm_financials ?? 0} />
        <Counter label="Capital" value={crossRefs?.capital_sources ?? 0} />
      </div>
      {totalRefs === 0 && (
        <div className="text-[11px] text-zinc-500 italic" data-testid="entity-connectivity-empty">
          Aucune référence cross-module pour le moment. Plus l'entité est citée dans vos risques / chaînes / opportunités, plus elle gagne en intégration.
        </div>
      )}

      {/* Signaux récents */}
      {sigItems.length > 0 && (
        <div data-testid="entity-connectivity-signals-section">
          <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold text-[#EF9F27]">
            <Radio className="w-3 h-3" />
            <span>Signaux récents mentionnant « {entityName} »</span>
            <span className="text-zinc-600 normal-case tracking-normal">· {sigItems.length}</span>
          </div>
          <div className="space-y-1.5">
            {sigItems.slice(0, 6).map((s) => (
              <a
                key={s.signal_id}
                href={s.signal_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-2 py-1.5 rounded hover:bg-white/[0.04] border border-transparent hover:border-white/10 group"
                data-testid={`entity-connectivity-signal-${s.signal_id}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold flex-shrink-0 mt-0.5" style={{ color: s.signal_type === "trend" ? "#7F77DD" : "#378ADD", background: s.signal_type === "trend" ? "#7F77DD1f" : "#378ADD1f" }}>
                    {s.signal_type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-zinc-200 truncate group-hover:text-white">{s.signal_title}</div>
                    {s.snippet && <div className="text-[10px] text-zinc-500 truncate italic mt-0.5">{s.snippet}</div>}
                  </div>
                  {s.signal_url && <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-zinc-300 flex-shrink-0 mt-1" />}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Actifs des modules îlots */}
      {assetTotals.total > 0 && (
        <div data-testid="entity-connectivity-assets-section">
          <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold text-[#1D9E75]">
            <Briefcase className="w-3 h-3" />
            <span>Actifs rattachés</span>
            <span className="text-zinc-600 normal-case tracking-normal">· {assetTotals.total}</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <AssetGroup icon={Building2} label="Immobilier" items={assets.re_assets || []} keyLabel="label" />
            <AssetGroup icon={Coins} label="Marchés financiers" items={assets.brvm_financials || []} keyLabel="name" />
            <AssetGroup icon={Briefcase} label="Sources Capital" items={assets.capital_sources || []} keyLabel="name" />
          </div>
        </div>
      )}
    </div>
  );
}

function Counter({ label, value, highlight = false }) {
  return (
    <div className={`px-2 py-1.5 rounded text-center ${highlight ? "bg-[#EF9F27]/10 border border-[#EF9F27]/30" : "bg-white/[0.03] border border-white/5"}`}>
      <div className={`text-sm font-bold leading-none ${highlight ? "text-[#EF9F27]" : value > 0 ? "text-zinc-200" : "text-zinc-600"}`}>{value}</div>
      <div className="text-[9px] text-zinc-500 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function AssetGroup({ icon: Icon, label, items, keyLabel }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border border-white/5 rounded p-2">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3 h-3 text-[#378ADD]" />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-300">{label}</span>
        <span className="text-[10px] text-zinc-600">· {items.length}</span>
      </div>
      <div className="space-y-0.5">
        {items.slice(0, 3).map((item) => (
          <div key={item.id} className="text-[10px] text-zinc-400 truncate">{item[keyLabel] || item.label || item.name || item.id}</div>
        ))}
        {items.length > 3 && <div className="text-[9px] text-zinc-600 italic">+ {items.length - 3} autres</div>}
      </div>
    </div>
  );
}
